/**
 * Finance data access.
 *
 * Every call goes through `apiFetch`, which owns cookies, idempotency keys, the RFC 9457
 * problem shape and the step-up retry. Nothing here touches fetch.
 *
 * Freshness class is `snapshot` for everything on these pages: money analytics refetch on
 * focus and go stale after 15 minutes, per DESIGN.md section 6. The forecast is `model` and
 * is fetched on demand only, because a cash model that silently re-runs while the owner is
 * reading it is a cash model nobody trusts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WindowKind } from '@elutide/shared';
import { apiFetch } from '@/lib/api';
import type {
  CloseResponse,
  FinanceOverviewResponse,
  FixedCostPage,
  ForecastResponse,
  HeldFundRow,
  Page,
  PnlResponse,
  ProductPnlRow,
  TaxFilingRow,
  WorkingCapital,
} from './types';

/**
 * Query keys.
 *
 * `qk` in `@/lib/keys` is A4's and carries no finance entries yet, so these follow the same
 * convention locally: a stable root, then the parameters that change the answer. The
 * /changes poller cannot reach them until the root is added there; raised in
 * INTEGRATION_NOTES.
 */
export const financeKeys = {
  root: ['finance'] as const,
  overview: (window: WindowKind) => ['finance', 'overview', window] as const,
  workingCapital: () => ['finance', 'working-capital'] as const,
  forecast: (horizon: number, scenario: string) => ['finance', 'forecast', horizon, scenario] as const,
  pnl: (window: WindowKind) => ['finance', 'pnl', window] as const,
  productPnl: (window: WindowKind, limit: number) => ['finance', 'product-pnl', window, limit] as const,
  fixedCosts: (includeInactive: boolean) => ['finance', 'ledgers', 'fixed-costs', includeInactive] as const,
  heldFunds: () => ['finance', 'ledgers', 'held-funds'] as const,
  taxFilings: () => ['finance', 'ledgers', 'tax-filings'] as const,
  close: (period: string) => ['finance', 'close', period] as const,
} as const;

const SNAPSHOT_STALE_MS = 15 * 60 * 1000;

/* ------------------------------------------------------------------ */
/* Normalisation                                                       */
/* ------------------------------------------------------------------ */

/**
 * The ledger endpoints return their `date` columns straight off the driver, so a value
 * that API.md types as a NyDate (`2026-08-20`) can arrive as a full instant
 * (`2026-08-20T04:00:00.000Z`). The two ledgers that hand back raw rows are normalised
 * here rather than in six render functions, and the fields the server derives from those
 * same dates are derived again from the normalised value: on the wire they come back
 * `null` whenever the server's own string parse failed, and a null age is not an age.
 *
 * Slicing is safe either way. The first ten characters of a NyDate are the NyDate.
 */
const nyDate = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  const text = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
};

const nyToday = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

/** Whole days from `from` to `to`, both YYYY-MM-DD. */
const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

function normalizeTaxFiling(row: TaxFilingRow): TaxFilingRow {
  const today = nyToday();
  const due = nyDate(row.due_date);
  const filed = nyDate(row.filed_date);
  return {
    ...row,
    period_start: nyDate(row.period_start) ?? row.period_start,
    period_end: nyDate(row.period_end) ?? row.period_end,
    due_date: due ?? row.due_date,
    filed_date: filed,
    paid_date: nyDate(row.paid_date),
    // The list route derives `owed_cents`; the PATCH route hands back the stored row
    // without it. Same arithmetic, so the row a write returns reads like the row the list
    // returned rather than showing a blank where the balance was.
    owed_cents: Number.isFinite(row.owed_cents)
      ? row.owed_cents
      : Number(row.collected_cents) + Number(row.adjustments_cents) - Number(row.remitted_cents),
    days_until_due: due === null ? null : daysBetween(today, due),
    overdue: due !== null && due < today && filed === null,
  };
}

function normalizeHeldFund(row: HeldFundRow): HeldFundRow {
  const today = nyToday();
  const expected = nyDate(row.expected_release_date);
  return {
    ...row,
    held_date: nyDate(row.held_date) ?? row.held_date,
    expected_release_date: expected,
    actual_release_date: nyDate(row.actual_release_date),
    days_past_expected: expected !== null && expected < today ? daysBetween(expected, today) : 0,
  };
}

export function useFinanceOverview(window: WindowKind) {
  return useQuery({
    queryKey: financeKeys.overview(window),
    queryFn: () => apiFetch<FinanceOverviewResponse>('/finance/overview', { query: { window } }),
    staleTime: SNAPSHOT_STALE_MS,
    refetchOnWindowFocus: true,
  });
}

export function useWorkingCapital() {
  return useQuery({
    queryKey: financeKeys.workingCapital(),
    queryFn: () => apiFetch<WorkingCapital>('/finance/working-capital'),
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function useForecast(horizon: number, scenario: string) {
  return useQuery({
    queryKey: financeKeys.forecast(horizon, scenario),
    queryFn: () => apiFetch<ForecastResponse>('/finance/forecast', { query: { horizon, scenario } }),
    // The model class is fetched on demand. It does not refetch on focus, and its age is
    // reported as the snapshot it was built from rather than as a live figure.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

export function usePnl(window: WindowKind) {
  return useQuery({
    queryKey: financeKeys.pnl(window),
    queryFn: () => apiFetch<PnlResponse>('/finance/pnl', { query: { window } }),
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function useProductPnl(window: WindowKind, limit = 50) {
  return useQuery({
    queryKey: financeKeys.productPnl(window, limit),
    queryFn: () => apiFetch<Page<ProductPnlRow>>('/finance/product-pnl', { query: { window, limit } }),
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function useFixedCosts(includeInactive = false) {
  return useQuery({
    queryKey: financeKeys.fixedCosts(includeInactive),
    queryFn: () =>
      apiFetch<FixedCostPage>('/finance/ledgers/fixed-costs', {
        query: { include_inactive: includeInactive, limit: 200 },
      }),
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function useHeldFunds() {
  return useQuery({
    queryKey: financeKeys.heldFunds(),
    queryFn: async () => {
      const page = await apiFetch<Page<HeldFundRow>>('/finance/ledgers/held-funds', { query: { limit: 200 } });
      return { ...page, items: (page.items ?? []).map(normalizeHeldFund) };
    },
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function useTaxFilings() {
  return useQuery({
    queryKey: financeKeys.taxFilings(),
    queryFn: async () => {
      const page = await apiFetch<Page<TaxFilingRow>>('/finance/ledgers/tax-filings', { query: { limit: 200 } });
      return { ...page, items: (page.items ?? []).map(normalizeTaxFiling) };
    },
    staleTime: SNAPSHOT_STALE_MS,
  });
}

export function useMonthClose(period: string) {
  return useQuery({
    queryKey: financeKeys.close(period),
    queryFn: () => apiFetch<CloseResponse>(`/finance/close/${period}`),
    staleTime: SNAPSHOT_STALE_MS,
  });
}

/* ------------------------------------------------------------------ */
/* Mutations                                                           */
/* ------------------------------------------------------------------ */

/**
 * Every mutation here moves money or a money schedule, so none of them is optimistic. They
 * show a pending state, wait for the server, and render what the server returned. Optimism
 * on a ledger write is how a figure ends up on screen that never reached the database.
 */

export interface MarkFiledInput {
  id: string;
  filed_date: string;
  remitted_cents: number;
  confirmation_number: string | null;
  paid_date: string | null;
  reason: string;
}

export function useMarkTaxFiled() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: MarkFiledInput) =>
      normalizeTaxFiling(
        await apiFetch<TaxFilingRow>(`/finance/ledgers/tax-filings/${input.id}`, {
          method: 'PATCH',
          body: {
            mark_filed: {
              filed_date: input.filed_date,
              remitted_cents: input.remitted_cents,
              confirmation_number: input.confirmation_number,
              paid_date: input.paid_date,
            },
            reason: input.reason,
          },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: financeKeys.taxFilings() });
      void client.invalidateQueries({ queryKey: financeKeys.root });
    },
  });
}

export function useRecomputeTaxFiling() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) =>
      normalizeTaxFiling(
        await apiFetch<TaxFilingRow>(`/finance/ledgers/tax-filings/${input.id}`, {
          method: 'PATCH',
          body: { recompute: true, reason: input.reason },
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: financeKeys.taxFilings() });
    },
  });
}

export interface FixedCostInput {
  name: string;
  category: string;
  amount_cents: number;
  frequency: string;
  start_date: string;
  anchor_day: number | null;
  vendor: string | null;
  include_in_pnl: boolean;
  include_in_cash_forecast: boolean;
  notes: string | null;
}

export function useCreateFixedCost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: FixedCostInput) =>
      apiFetch('/finance/ledgers/fixed-costs', { method: 'POST', body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: financeKeys.root });
    },
  });
}

export function useDeleteFixedCost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      apiFetch(`/finance/ledgers/fixed-costs/${input.id}`, {
        method: 'DELETE',
        body: { reason: input.reason },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: financeKeys.root });
    },
  });
}

export function useHeldFundPatch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string } & Record<string, unknown>) => {
      const { id, ...body } = input;
      return normalizeHeldFund(
        await apiFetch<HeldFundRow>(`/finance/ledgers/held-funds/${id}`, { method: 'PATCH', body }),
      );
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: financeKeys.heldFunds() });
    },
  });
}

export interface PayoutImportResult {
  payout_id: string;
  matched: number;
  unmatched: number;
  rejected_rows: { row_number: number; reason: string }[];
  totals: Record<string, number>;
  held_fund_id: string | null;
  fee_actuals_written: number;
  exceptions: Record<string, unknown>[];
  dry_run?: boolean;
  parsed_lines?: number;
}

export function useImportPayout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { csv: string; dry_run: boolean; payout_date?: string }) =>
      apiFetch<PayoutImportResult>('/finance/payouts/import', {
        method: 'POST',
        body: { csv: input.csv, dry_run: input.dry_run, ...(input.payout_date ? { payout_date: input.payout_date } : {}) },
      }),
    onSuccess: (_result, variables) => {
      if (!variables.dry_run) void client.invalidateQueries({ queryKey: financeKeys.root });
    },
  });
}

export function useMarkPeriodReviewed(period: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { reason: string; note: string | null }) =>
      apiFetch<{ locked: boolean; reviewed: boolean }>(`/finance/close/${period}`, {
        method: 'POST',
        body: { typed_confirm: period, reason: input.reason, note: input.note },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: financeKeys.close(period) });
    },
  });
}

export interface ExportResult {
  export_id: string;
  url: string | null;
  filename: string;
  content_type: string;
  bytes: number;
  csv: string;
}

export function useFinanceExport() {
  return useMutation({
    mutationFn: (input: { report: string; reason: string; filters?: Record<string, unknown> }) =>
      apiFetch<ExportResult>('/finance/export', {
        method: 'POST',
        body: { report: input.report, reason: input.reason, filters: input.filters ?? {} },
      }),
  });
}
